import { useState } from 'react';
import { useAnimation } from '@/context/AnimationContext';
import { useI18n } from '@/context/I18nContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

const LIBRARY_CATEGORIES = [
  {
    id: 'shapes',
    items: [
      { id: 'rect', name: 'rectangle', type: 'rect', width: 100, height: 80, fill: '#e879a0', preview: (c) => <rect x="6" y="10" width="28" height="20" rx="2" fill={c}/> },
      { id: 'circle', name: 'circle', type: 'circle', width: 60, height: 60, fill: '#f472b6', preview: (c) => <circle cx="20" cy="20" r="14" fill={c}/> },
      { id: 'ellipse', name: 'ellipse', type: 'ellipse', width: 90, height: 50, fill: '#a78bfa', preview: (c) => <ellipse cx="20" cy="20" rx="16" ry="10" fill={c}/> },
      { id: 'star5', name: 'star', type: 'star', width: 50, height: 50, fill: '#fbbf24', numPoints: 5, innerRadius: 15, outerRadius: 30, preview: (c) => <polygon points="20,4 24,15 36,15 27,22 30,33 20,26 10,33 13,22 4,15 16,15" fill={c}/> },
      { id: 'triangle', name: 'triangle', type: 'path', width: 80, height: 70, fill: '#67e8f9', stroke: '#67e8f9', strokeWidth: 3, points: [0,-35, 30,25, -30,25, 0,-35], preview: (c) => <polygon points="20,4 36,34 4,34" fill={c}/> },
      { id: 'hexagon', name: 'hexagon', type: 'path', width: 60, height: 52, fill: '#34d399', stroke: '#34d399', strokeWidth: 3, points: [0,-26, 22.5,-13, 22.5,13, 0,26, -22.5,13, -22.5,-13, 0,-26], preview: (c) => <polygon points="20,4 34,12 34,28 20,36 6,28 6,12" fill={c}/> },
      { id: 'diamond', name: 'diamond', type: 'path', width: 50, height: 70, fill: '#818cf8', stroke: '#818cf8', strokeWidth: 3, points: [0,-35, 25,0, 0,35, -25,0, 0,-35], preview: (c) => <polygon points="20,3 36,20 20,37 4,20" fill={c}/> },
      { id: 'pentagon', name: 'pentagon', type: 'path', width: 56, height: 54, fill: '#fb923c', stroke: '#fb923c', strokeWidth: 3, points: [0,-27, 25.7,-8.3, 15.9,21.8, -15.9,21.8, -25.7,-8.3, 0,-27], preview: (c) => <polygon points="20,4 36,16 30,34 10,34 4,16" fill={c}/> },
    ]
  },
  {
    id: 'decorations',
    items: [
      { id: 'heart', name: 'heart', type: 'path', width: 60, height: 55, fill: '#f43f5e', stroke: '#f43f5e', strokeWidth: 3,
        points: [0,12, -2,10, -7,5, -12,0, -15,-3, -15,-8, -13,-12, -9,-14, -5,-13, -2,-10, 0,-7, 2,-10, 5,-13, 9,-14, 13,-12, 15,-8, 15,-3, 12,0, 7,5, 2,10, 0,12],
        preview: (c) => <path d="M20 34 C20 34 4 22 4 13 C4 7 9 3 14 6 L20 12 L26 6 C31 3 36 7 36 13 C36 22 20 34 20 34Z" fill={c}/> },
      { id: 'arrow-right', name: 'arrowRight', type: 'path', width: 80, height: 40, fill: '#38bdf8', stroke: '#38bdf8', strokeWidth: 3,
        points: [-40,0, 10,0, 10,-15, 40,0, 10,15, 10,0],
        preview: (c) => <polygon points="4,16 24,16 24,8 38,20 24,32 24,24 4,24" fill={c}/> },
      { id: 'arrow-up', name: 'arrowUp', type: 'path', width: 40, height: 80, fill: '#a78bfa', stroke: '#a78bfa', strokeWidth: 3,
        points: [0,-40, 0,-10, 15,-10, 0,40, -15,-10, 0,-10],
        preview: (c) => <polygon points="20,2 20,16 28,16 20,38 12,16 20,16" fill={c} transform="rotate(0,20,20)"/> },
      { id: 'lightning', name: 'lightning', type: 'path', width: 40, height: 70, fill: '#facc15', stroke: '#facc15', strokeWidth: 2,
        points: [5,-35, -10,-5, 5,-5, -5,35, 15,0, 0,0, 15,-35],
        preview: (c) => <polygon points="22,2 10,18 20,18 14,38 28,16 18,16 26,2" fill={c}/> },
      { id: 'cloud', name: 'cloud', type: 'ellipse', width: 100, height: 50, fill: '#e2e8f0',
        preview: (c) => <><ellipse cx="20" cy="22" rx="12" ry="8" fill={c}/><ellipse cx="14" cy="20" rx="8" ry="6" fill={c}/><ellipse cx="26" cy="20" rx="8" ry="6" fill={c}/><ellipse cx="20" cy="16" rx="7" ry="5" fill={c}/></> },
      { id: 'flower', name: 'flower', type: 'star', width: 50, height: 50, fill: '#f9a8d4', numPoints: 6, innerRadius: 10, outerRadius: 24,
        preview: (c) => <><circle cx="20" cy="10" r="6" fill={c} opacity="0.7"/><circle cx="29" cy="15" r="6" fill={c} opacity="0.7"/><circle cx="29" cy="25" r="6" fill={c} opacity="0.7"/><circle cx="20" cy="30" r="6" fill={c} opacity="0.7"/><circle cx="11" cy="25" r="6" fill={c} opacity="0.7"/><circle cx="11" cy="15" r="6" fill={c} opacity="0.7"/><circle cx="20" cy="20" r="4" fill="#fbbf24"/></> },
    ]
  },
  {
    id: 'ballet',
    items: [
      { id: 'music-note', name: 'musicNote', type: 'path', width: 30, height: 50, fill: '#c084fc', stroke: '#c084fc', strokeWidth: 3,
        points: [0,20, 0,-20, 15,-25, 15,-15, 0,-10],
        preview: (c) => <><line x1="16" y1="8" x2="16" y2="30" stroke={c} strokeWidth="2.5"/><circle cx="12" cy="30" r="5" fill={c}/><path d="M16 8 L28 5 L28 12 L16 15Z" fill={c}/></> },
      { id: 'spotlight', name: 'spotlight', type: 'path', width: 80, height: 100, fill: '#fef3c7', stroke: '#fef3c7', strokeWidth: 2,
        points: [0,-50, -30,40, -25,42, 25,42, 30,40, 0,-50],
        preview: (c) => <polygon points="20,4 6,36 34,36" fill={c} opacity="0.4"/> },
      { id: 'curtain-l', name: 'curtainLeft', type: 'rect', width: 60, height: 200, fill: '#991b1b',
        preview: (c) => <><rect x="4" y="4" width="14" height="32" rx="1" fill={c}/><rect x="4" y="4" width="14" height="4" fill={c} opacity="0.5"/></> },
      { id: 'curtain-r', name: 'curtainRight', type: 'rect', width: 60, height: 200, fill: '#991b1b',
        preview: (c) => <><rect x="22" y="4" width="14" height="32" rx="1" fill={c}/><rect x="22" y="4" width="14" height="4" fill={c} opacity="0.5"/></> },
      { id: 'stage-floor', name: 'stageFloor', type: 'rect', width: 600, height: 10, fill: '#b8956a',
        preview: (c) => <rect x="4" y="16" width="32" height="6" rx="1" fill={c} opacity="0.6"/> },
      { id: 'star-sparkle', name: 'sparkle', type: 'star', width: 20, height: 20, fill: '#fde68a', numPoints: 4, innerRadius: 4, outerRadius: 10,
        preview: (c) => <polygon points="20,4 22,16 34,20 22,24 20,36 18,24 6,20 18,16" fill={c}/> },
    ]
  },
];

export function ObjectLibrary({ trigger }) {
  const [open, setOpen] = useState(false);
  const { state, dispatch } = useAnimation();
  const { t } = useI18n();

  const addLibraryObject = (item) => {
    const targetLayer = state.selectedLayerId || state.project.layers[0]?.id;
    if (!targetLayer) return;

    const cx = state.project.width / 2;
    const cy = state.project.height / 2;

    const obj = {
      id: `obj-lib-${item.id}-${Date.now()}`,
      type: item.type,
      name: t(item.name) || item.name,
      x: cx,
      y: cy,
      width: item.width,
      height: item.height,
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      opacity: 1,
      fill: item.fill,
      stroke: item.stroke || '',
      strokeWidth: item.strokeWidth || 0,
      keyframes: [
        { frame: 0, x: cx, y: cy, rotation: 0, scaleX: 1, scaleY: 1, opacity: 1 }
      ],
    };

    if (item.numPoints) obj.numPoints = item.numPoints;
    if (item.innerRadius) obj.innerRadius = item.innerRadius;
    if (item.outerRadius) obj.outerRadius = item.outerRadius;
    if (item.points) obj.points = item.points;
    if (item.text) obj.text = item.text;
    if (item.fontSize) obj.fontSize = item.fontSize;

    dispatch({ type: 'ADD_OBJECT', payload: { layerId: targetLayer, object: obj } });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-[hsl(220,14%,11%)] border-white/[0.08]" data-testid="object-library-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-sm">{t('objectLibrary')}</DialogTitle>
            <DialogDescription className="text-[10px] text-muted-foreground/60">{t('objectLibraryHint')}</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-4 pr-2">
              {LIBRARY_CATEGORIES.map(cat => (
                <div key={cat.id} data-testid={`library-category-${cat.id}`}>
                  <p className="text-[9px] font-medium text-muted-foreground/50 uppercase tracking-[0.15em] mb-2">
                    {t(`libCat_${cat.id}`)}
                  </p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {cat.items.map(item => (
                      <button
                        key={item.id}
                        className="group flex flex-col items-center gap-1 p-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.06] border border-white/[0.04] hover:border-rose-500/20 transition-all cursor-pointer"
                        onClick={() => addLibraryObject(item)}
                        data-testid={`library-item-${item.id}`}
                      >
                        <svg viewBox="0 0 40 40" className="w-8 h-8 transition-transform group-hover:scale-110">
                          {item.preview(item.fill)}
                        </svg>
                        <span className="text-[8px] font-mono text-muted-foreground/60 group-hover:text-muted-foreground truncate max-w-full">
                          {t(item.name) || item.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
  );
}
